import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore - HLS.js for FFmpeg transcoded streams
import Hls from 'hls.js';
import CompactVolumeSlider from './CompactVolumeSlider';
import { subscribeToVolume, setGlobalVolume } from '../utils/globalVolume';
const StreamTile = ({ streamId, proxyUrl, directUrl, title, featuredMode = false, isHost = false, socket, isGloballyMuted = false }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const stallCheckRef = useRef(null);
    const lastTimeRef = useRef(0);
    const [volume, setVolume] = useState(0); // Synced with global volume
    const [isHovered, setIsHovered] = useState(false);
    // Subscribe to global volume changes
    useEffect(() => {
        const unsubscribe = subscribeToVolume((newVolume) => {
            setVolume(newVolume);
        });
        return unsubscribe;
    }, []);
    console.log('ðŸ“º StreamTile render:', { title, directUrl, proxyUrl });
    // Stall detection - checks if video is stuck
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isPlaying)
            return;
        const checkStall = () => {
            const currentTime = video.currentTime;
            // If time hasn't changed in 5 seconds and we're supposed to be playing
            if (currentTime === lastTimeRef.current && !video.paused && !video.ended) {
                console.warn('âš ï¸ Stream appears stalled, attempting recovery...');
                if (hlsRef.current) {
                    hlsRef.current.startLoad();
                }
            }
            lastTimeRef.current = currentTime;
        };
        // Check every 5 seconds
        stallCheckRef.current = window.setInterval(checkStall, 5000);
        return () => {
            if (stallCheckRef.current) {
                clearInterval(stallCheckRef.current);
            }
        };
    }, [isPlaying]);
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        // Get the stream URL
        let streamUrl = directUrl || proxyUrl;
        // Convert old M3U8 generate URLs to HLS format
        if (streamUrl.includes('/api/stream/generate.m3u8')) {
            const urlParams = new URLSearchParams(streamUrl.split('?')[1]);
            const baseUrl = urlParams.get('base');
            if (baseUrl) {
                const streamIdMatch = baseUrl.match(/\/stream\/([^\/]+)/);
                if (streamIdMatch) {
                    streamUrl = `/hls/${streamIdMatch[1]}/stream.m3u8`;
                }
            }
        }
        console.log('ðŸ”§ Initializing HLS.js player for:', streamUrl);
        // HLS.js support check
        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false, // Changed: disable low latency to reduce CPU
                backBufferLength: 30, // Changed: reduced from 90
                maxBufferLength: 20, // Changed: reduced from 30
                maxMaxBufferLength: 60, // Changed: reduced from 600
                maxBufferSize: 30 * 1000 * 1000, // Changed: reduced
                maxBufferHole: 1, // Changed: increased tolerance
                highBufferWatchdogPeriod: 3, // Changed: less aggressive
                nudgeOffset: 0.5, // Changed: increased
                nudgeMaxRetry: 5, // Changed: more retries
                maxFragLookUpTolerance: 0.5, // Changed: more tolerance
                liveSyncDurationCount: 2,
                liveMaxLatencyDurationCount: 6, // Changed: reduced
                liveDurationInfinity: true,
                liveBackBufferLength: 0
            });
            hlsRef.current = hls;
            hls.loadSource(streamUrl);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('âœ… HLS manifest parsed, starting playback');
                video.play()
                    .then(() => {
                    console.log('âœ… HLS playback started');
                    setIsPlaying(true);
                    setError(null);
                })
                    .catch(err => {
                    console.error('âŒ HLS playback failed:', err);
                    setError('Playback failed - click to retry');
                });
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('âŒ HLS error:', data);
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.log('ðŸ”„ Network error, trying to recover...');
                            if (retryCount < 10) {
                                setTimeout(() => {
                                    hls.startLoad();
                                    setRetryCount(prev => prev + 1);
                                }, 1000);
                            }
                            else {
                                setError('Network error - click to retry');
                            }
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log('ðŸ”„ Media error, trying to recover...');
                            if (retryCount < 10) {
                                hls.recoverMediaError();
                                setRetryCount(prev => prev + 1);
                            }
                            else {
                                setError('Media error - click to retry');
                            }
                            break;
                        default:
                            console.error('ðŸ’¥ Fatal error, will auto-retry in 3 seconds...');
                            setError('Stream error - retrying...');
                            // Auto-retry after 3 seconds
                            setTimeout(() => {
                                console.log('ðŸ”„ Auto-restarting stream...');
                                hls.destroy();
                                window.location.reload();
                            }, 3000);
                            break;
                    }
                }
                else {
                    // Non-fatal errors - just log them
                    console.warn('âš ï¸ Non-fatal HLS error:', data.details);
                }
            });
            // Additional event listeners for debugging
            hls.on(Hls.Events.FRAG_LOADED, () => {
                // Reset retry count on successful fragment load
                if (retryCount > 0) {
                    setRetryCount(0);
                }
            });
            return () => {
                console.log('ðŸ§¹ Cleaning up HLS.js');
                if (hlsRef.current) {
                    hlsRef.current.destroy();
                    hlsRef.current = null;
                }
            };
        }
        // Safari native HLS support
        else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            console.log('ðŸŽ Using Safari native HLS');
            video.src = streamUrl;
            const handleLoadedMetadata = () => {
                console.log('âœ… Safari HLS metadata loaded');
                video.play()
                    .then(() => {
                    console.log('âœ… Safari HLS playback started');
                    setIsPlaying(true);
                    setError(null);
                })
                    .catch(err => {
                    console.error('âŒ Safari HLS playback failed:', err);
                    setError('Playback failed - click to retry');
                });
            };
            const handleError = () => {
                console.error('âŒ Safari HLS error');
                setError('Stream error - click to retry');
            };
            video.addEventListener('loadedmetadata', handleLoadedMetadata);
            video.addEventListener('error', handleError);
            return () => {
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('error', handleError);
                video.src = '';
            };
        }
        else {
            console.error('âŒ HLS not supported in this browser');
            setError('HLS not supported in this browser');
            return;
        }
    }, [proxyUrl, directUrl, retryCount]);
    const handleRetry = () => {
        console.log('ðŸ”„ Manual retry requested...');
        setRetryCount(0);
        setError(null);
        window.location.reload();
    };
    // Host click control - pause/play stream
    const handleTileClick = () => {
        if (!isHost || !socket || !videoRef.current) {
            console.log('ðŸš« Non-host click ignored or no video ready');
            return;
        }
        const video = videoRef.current;
        const isPaused = video.paused;
        console.log(`ðŸŽ¬ StreamTile host click - current state: ${isPaused ? 'paused' : 'playing'}`);
        if (isPaused) {
            // Currently paused - play and jump to live edge
            console.log('â–¶ï¸ Host clicking to PLAY stream for all users (jump to live edge)');
            socket.emit('stream-control', { streamId, action: 'play' });
        }
        else {
            // Currently playing - pause it
            console.log('â¸ï¸ Host clicking to PAUSE stream for all users');
            socket.emit('stream-control', { streamId, action: 'pause' });
        }
    };
    // Listen for stream control broadcasts
    useEffect(() => {
        if (!socket)
            return;
        const handleStreamControlBroadcast = (data) => {
            console.log('ðŸ“º StreamTile received control broadcast:', data);
            if (data.streamId !== streamId)
                return;
            const video = videoRef.current;
            if (!video)
                return;
            try {
                switch (data.action) {
                    case 'play':
                        console.log('â–¶ï¸ Playing stream via broadcast (jumping to live edge)');
                        // Jump to live edge before playing
                        if (hlsRef.current && hlsRef.current.liveSyncPosition !== undefined) {
                            video.currentTime = hlsRef.current.liveSyncPosition;
                            console.log(`ðŸ”´ Jumped to live edge: ${hlsRef.current.liveSyncPosition.toFixed(2)}s`);
                        }
                        video.play()
                            .then(() => {
                            setIsPlaying(true);
                            console.log('âœ… Stream playing at live edge');
                        })
                            .catch(err => console.error('âŒ Play failed:', err));
                        break;
                    case 'pause':
                        console.log('â¸ï¸ Pausing stream via broadcast');
                        video.pause();
                        setIsPlaying(false);
                        break;
                    default:
                        console.log('â“ Unknown stream control action:', data.action);
                }
            }
            catch (error) {
                console.error('âŒ Error executing stream control:', error);
            }
        };
        socket.on('stream-control', handleStreamControlBroadcast);
        return () => socket.off('stream-control', handleStreamControlBroadcast);
    }, [socket, streamId]);
    // Handle volume changes and mute state
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        video.volume = volume / 100;
        video.muted = volume === 0;
        console.log(`ðŸ”Š Stream volume set to ${volume}% (${volume === 0 ? 'muted' : 'unmuted'})`);
    }, [volume]);
    return (_jsxs("div", { className: `stream-tile ${featuredMode ? 'featured' : ''}`, style: {
            width: featuredMode ? '854px' : '310px',
            height: featuredMode ? '480px' : '180px',
            border: '1px solid #8b5cf6',
            borderRadius: '3px',
            overflow: 'hidden',
            backgroundColor: '#000',
            cursor: isHost ? 'pointer' : 'default',
            position: 'relative'
        }, onClick: handleTileClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: [_jsx("video", { ref: videoRef, style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                }, playsInline: true, muted: volume === 0, controls: false }), error && (_jsxs("div", { style: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    cursor: 'pointer'
                }, onClick: handleRetry, children: [_jsx("div", { style: { marginBottom: '8px' }, children: "\u00E2\u0161\u00A0\u00EF\u00B8\u008F" }), _jsx("div", { style: { fontSize: '12px', marginBottom: '8px' }, children: error }), _jsx("div", { style: {
                            fontSize: '11px',
                            color: '#8b5cf6', // âœ… CHANGED: Purple accent (was #ef4444)
                            textDecoration: 'underline'
                        }, children: "Click to retry" })] })), _jsx("div", { style: {
                    opacity: volume === 0 ? 1 : (isHovered ? 1 : 0),
                    transition: 'opacity 0.2s ease',
                    pointerEvents: volume === 0 ? 'auto' : (isHovered ? 'auto' : 'none')
                }, children: _jsx(CompactVolumeSlider, { volume: volume, onVolumeChange: setGlobalVolume, isFeatured: featuredMode }) })] }));
};
export default StreamTile;
